import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportReason, SwipeDirection } from '@sfc/sdk';
import { platform } from '../api/client';
import { ReportPanel } from '../safety/ReportPanel';
import { Card } from './Card';
import { useDeck } from './useDeck';

/** Far enough to be a decision rather than a nudge, on any width of screen. */
const threshold = (width: number): number => Math.max(64, width * 0.25);

/** How long the card is given to leave. Zero for anyone who asked for less motion. */
function exitDuration(): number {
  if (typeof window.matchMedia !== 'function') return 190;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 190;
}

/**
 * The deck.
 *
 * Three ways to decide, not one: drag it, press an arrow key, or press a
 * button. A deck that only answers to dragging is unusable to anyone on a
 * keyboard, anyone using a switch or a screen reader, and anyone whose hands
 * do not do a precise 90-pixel swipe on demand. The buttons are not a fallback
 * hidden behind a menu; they sit under the card where a thumb already is.
 *
 * All three paths meet at `commit`, so the card leaves the same way and the
 * decision is recorded the same way whichever one somebody used.
 */
export function DeckScreen({ onChooseSkills }: { onChooseSkills: () => void }): React.JSX.Element {
  const { state, decide, refresh } = useDeck();
  const [leaving, setLeaving] = useState<{ ideaId: string; direction: SwipeDirection }>();
  const [offset, setOffset] = useState(0);
  const [said, setSaid] = useState('');
  const [reporting, setReporting] = useState(false);
  /** How many this person has decided here. Only used to retire the hint. */
  const [decided, setDecided] = useState(0);
  const seenEnough = decided >= 3;
  const from = useRef<number | undefined>(undefined);

  const top = state.status === 'ready' ? state.cards[0] : undefined;
  const next = state.status === 'ready' ? state.cards[1] : undefined;

  const commit = useCallback(
    (ideaId: string, direction: SwipeDirection, title: string) => {
      setLeaving((current) => (current ? current : { ideaId, direction }));
      setSaid(direction === 'right' ? `Interested in ${title}.` : `Passed on ${title}.`);
      setDecided((count) => count + 1);
    },
    [],
  );

  // The decision is recorded once the card has finished leaving, so the deck
  // that comes back does not replace the card mid-animation.
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => {
      decide(leaving.ideaId, leaving.direction);
      setLeaving(undefined);
      setOffset(0);
    }, exitDuration());
    return () => clearTimeout(timer);
  }, [leaving, decide]);

  useEffect(() => {
    if (!top || leaving || reporting) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      // Not while somebody is typing, and not while they are tabbing through
      // the buttons, where the browser's own Enter handling should win.
      if (target?.closest('input, textarea, select, button, [contenteditable]')) return;
      if (event.key === 'ArrowLeft') commit(top!.ideaId, 'left', top!.title);
      else if (event.key === 'ArrowRight') commit(top!.ideaId, 'right', top!.title);
      else return;
      event.preventDefault();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [top, leaving, reporting, commit]);

  const handlers: React.HTMLAttributes<HTMLElement> = {
    onPointerDown: (event) => {
      if (leaving || event.button !== 0) return;
      from.current = event.clientX;
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    },
    onPointerMove: (event) => {
      if (from.current === undefined) return;
      setOffset(event.clientX - from.current);
    },
    onPointerUp: (event) => {
      if (from.current === undefined) return;
      const travelled = event.clientX - from.current;
      from.current = undefined;
      const element = event.currentTarget as HTMLElement;
      if (top && Math.abs(travelled) >= threshold(element.offsetWidth)) {
        commit(top.ideaId, travelled > 0 ? 'right' : 'left', top.title);
        return;
      }
      setOffset(0);
    },
    onPointerCancel: () => {
      from.current = undefined;
      setOffset(0);
    },
  };

  return (
    <div className="deck">
      {state.status === 'loading' && (
        <p className="loading" data-testid="deck-loading">
          Finding ideas…
        </p>
      )}

      {state.status === 'unreachable' && (
        <div className="placeholder">
          <h2>We cannot reach the server</h2>
          <p className="notice notice--problem" role="status">
            Nothing you decided has been lost. Try again in a moment.
          </p>
          <button className="button" type="button" onClick={refresh}>
            Try again
          </button>
        </div>
      )}

      {state.status === 'ready' && (
        <section className="deck__body" aria-label="Ideas waiting for you">
          {/* Decisions are announced rather than only drawn, because the card
              leaving the screen is not an event a screen reader reports. */}
          <p className="sr-only" role="status" aria-live="polite" data-testid="announcement">
            {said}
          </p>

          {state.problem && (
            <p className="notice notice--problem" role="status" data-testid="deck-problem">
              {state.problem}
            </p>
          )}

          {top && reporting ? (
            <ReportPanel
              subject="this idea"
              onReport={(reason: ReportReason, detail: string) =>
                platform.match.reportIdea(top.ideaId, reason, detail || undefined)
              }
              onBlock={() => platform.match.blockAuthor(top.ideaId)}
              onClose={() => {
                setReporting(false);
                // Blocking takes their ideas out of the deck, so what is on
                // screen is out of date whichever button was pressed.
                refresh();
              }}
            />
          ) : top ? (
            <>
              <div className="stack">
                {/* Scenery: it says another one is coming, and nothing else.
                    Drawing the next card's contents under the one in front
                    shows a strip of somebody's half-read pitch, and leaves a
                    second copy of every label for a screen reader to find. */}
                {next && <div className="card card--behind" aria-hidden="true" />}
                <Card
                  key={top.ideaId}
                  card={top}
                  offset={offset}
                  {...(leaving?.ideaId === top.ideaId ? { leaving: leaving.direction } : {})}
                  dragging={from.current !== undefined}
                  handlers={handlers}
                />
              </div>

              <div className="choices">
                <button
                  className="button button--pass"
                  type="button"
                  data-testid="pass"
                  onClick={() => commit(top.ideaId, 'left', top.title)}
                >
                  Not for me
                </button>
                <button
                  className="button button--interested"
                  type="button"
                  data-testid="interested"
                  onClick={() => commit(top.ideaId, 'right', top.title)}
                >
                  I'm interested
                </button>
              </div>

              {/*
                One line, and only what is true right now. The keyboard hint
                is for the first few cards and then gets out of the way: it is
                help, and help that is still there on the fiftieth card is
                furniture. What stays is how many are left, because a reader
                deciding whether to keep going is asking exactly that.
              */}
              <div className="deck__foot">
                <span className="fine" data-testid="remaining">
                  {state.remaining === 1 ? '1 left' : `${state.remaining} left`}
                </span>
                {seenEnough ? null : (
                  <span className="fine deck__keys">
                    <kbd>←</kbd> <kbd>→</kbd> work too
                  </span>
                )}
                <button
                  className="button button--quiet deck__report"
                  type="button"
                  data-testid="report-idea"
                  onClick={() => setReporting(true)}
                >
                  Report
                </button>
              </div>

              {!state.rankedByYourSkills && (
                /*
                 * Said once, where it matters, and it is an invitation rather
                 * than a notice: without skills the deck is in date order,
                 * which is the product not doing the thing it exists for.
                 */
                <button
                  className="deck__nudge"
                  type="button"
                  data-testid="deck-nudge"
                  onClick={onChooseSkills}
                >
                  These are in date order. <strong>Say what you do</strong> and the ideas
                  asking for it come first.
                </button>
              )}
            </>
          ) : (
            <div className="placeholder" data-testid="deck-empty">
              <h2>You are all caught up</h2>
              <p>
                There is nothing new waiting for you. Ideas appear here as people post them, so
                it is worth looking again in a day or two.
              </p>
              <p className="fine">
                Anyone who accepts your interest will be able to reach you. Until then, nobody
                you passed on knows you saw them.
              </p>
              <button className="button" type="button" onClick={refresh} data-testid="recheck">
                Check again
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
